import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Circular seal component matching the WhatsApp PNG seal (responsive)
const CircularSeal = ({ percentage, shortHash, date }) => {
  // Use viewBox + preserveAspectRatio so it scales to container without cutting
  const viewSize = 280; // inner viewbox unit
  const cx = viewSize / 2;
  const cy = viewSize / 2;
  const innerR = 88; // dark inner circle
  const arcR = 100;  // colored arc - OUTSIDE dark circle, INSIDE text ring
  const arcThickness = 9;
  const ringR = 123; // text ring - outermost

  // Animated stroke-dashoffset (Apple-activity-ring feel)
  const [animatedPct, setAnimatedPct] = useState(0);
  const [displayedPct, setDisplayedPct] = useState(0);
  const [animDone, setAnimDone] = useState(false);
  const rafRef = useRef(null);
  useEffect(() => {
    setAnimDone(false);
    // Animate from 0 -> percentage over ~3.5s with a luxurious easeInOutQuart
    // Small delay before starting (mount settles, feels more deliberate)
    const startDelay = 250;
    const duration = 3500;
    const target = Math.max(0, Math.min(100, Number(percentage) || 0));
    let startTs = null;
    const tick = (now) => {
      if (startTs === null) startTs = now;
      const elapsed = now - startTs - startDelay;
      if (elapsed < 0) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const t = Math.min(1, elapsed / duration);
      // easeInOutQuart — silky, gentle both ends
      const eased = t < 0.5
        ? 8 * t * t * t * t
        : 1 - Math.pow(-2 * t + 2, 4) / 2;
      const v = target * eased;
      setAnimatedPct(v);
      setDisplayedPct(Math.round(v));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else setAnimDone(true);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [percentage]);

  // Color stops that paint the arc with a live gradient:
  // red (start) -> orange -> yellow -> green -> cyan -> blue (end)
  const isTop = percentage >= 90;
  const pctTextColor = isTop ? '#60a5fa' : percentage >= 50 ? '#22c55e' : '#f87171';

  const ringTextUnit = `VERIFIED BY W IA · WCLUB · #${shortHash} · ${date} · `;
  const fullText = ringTextUnit.repeat(3);

  // Conic gradient: starts red at 12 o'clock, rotates clockwise smoothly from
  // warm → cool, ending BLUE exactly at `animatedPct`. Rest is faint track.
  const p = Math.max(0.01, animatedPct);
  const conicBg = `conic-gradient(from 0deg,
    #ef4444 0%,
    #f97316 ${(p * 0.2).toFixed(2)}%,
    #eab308 ${(p * 0.4).toFixed(2)}%,
    #22c55e ${(p * 0.6).toFixed(2)}%,
    #06b6d4 ${(p * 0.8).toFixed(2)}%,
    #3b82f6 ${p.toFixed(2)}%,
    rgba(255,255,255,0.05) ${p.toFixed(2)}%,
    rgba(255,255,255,0.05) 100%)`;

  // Ring mask: transparent center + outer → black ring band ⇒ only the ring shows
  // Use percentages (relative to half-width) so it scales responsively
  const half = viewSize / 2;
  const pInner = ((arcR - arcThickness / 2 - 0.5) / half) * 100;
  const pInnerMid = ((arcR - arcThickness / 2) / half) * 100;
  const pOuterMid = ((arcR + arcThickness / 2) / half) * 100;
  const pOuter = ((arcR + arcThickness / 2 + 0.5) / half) * 100;
  const ringMask = `radial-gradient(circle, transparent ${pInner}%, #000 ${pInnerMid}%, #000 ${pOuterMid}%, transparent ${pOuter}%)`;

  return (
    <div className="relative mx-auto" style={{ width: '100%', maxWidth: 280, aspectRatio: '1' }}>
      {/* Inner conic gradient arc (hugs the dark circle) */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          background: conicBg,
          WebkitMask: ringMask,
          mask: ringMask,
          filter: 'drop-shadow(0 0 6px rgba(59,130,246,0.25))',
          pointerEvents: 'none',
        }}
      />
      <svg viewBox={`0 0 ${viewSize} ${viewSize}`} width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{ position: 'relative' }}>
        <defs>
          <radialGradient id="sealBg" cx="50%" cy="40%">
            <stop offset="0%" stopColor="#1a1d2c" />
            <stop offset="100%" stopColor="#08080c" />
          </radialGradient>
          <path id="ringPath" d={`M ${cx},${cy} m -${ringR},0 a ${ringR},${ringR} 0 1,1 ${ringR * 2},0 a ${ringR},${ringR} 0 1,1 -${ringR * 2},0`} fill="none" />
        </defs>
        {/* Outer shadow */}
        <circle cx={cx} cy={cy} r={ringR + 6} fill="rgba(0,0,0,0.6)" />
        {/* Inner circle with radial gradient */}
        <circle cx={cx} cy={cy} r={innerR} fill="url(#sealBg)" stroke="rgba(59,130,246,0.25)" strokeWidth="1.5" />
        {/* Circular ring text */}
        <text fill="rgba(255,255,255,0.65)" fontSize="9" fontWeight="700" fontFamily="Jost, sans-serif" letterSpacing="0.3">
          <textPath href="#ringPath" startOffset="0">
            {fullText}
          </textPath>
        </text>
        {/* Center W (gentle pulse when animation completes) */}
        <text x={cx} y={cy - 26} textAnchor="middle" fill="#ffffff" fontSize="46" fontWeight="800"
          fontFamily="'Jost', system-ui, -apple-system, Arial, sans-serif"
          style={{
            transformOrigin: `${cx}px ${cy - 22}px`,
            animation: animDone ? 'wSealPulse 1.6s ease-out 0.1s' : 'none',
          }}>W</text>
        {/* AUTHENTIC */}
        <text x={cx} y={cy + 4} textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="9" fontWeight="700" fontFamily="'Jost', system-ui, -apple-system, Arial, sans-serif" letterSpacing="2">AUTHENTIC</text>
        {/* Percentage (animated counter) */}
        <text x={cx} y={cy + 38} textAnchor="middle" fill={pctTextColor} fontSize="26" fontWeight="800" fontFamily="system-ui, -apple-system, 'Segoe UI', Arial, sans-serif" dominantBaseline="middle">{`${displayedPct}%`}</text>
      </svg>
    </div>
  );
};

const VerifyPage = () => {
  const { hash } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verify = async () => {
      try {
        // Sanitize: strip #, whitespace, invisible chars
        const cleanHash = (hash || '').replace(/[#\s\u200B-\u200D\uFEFF]/g, '').trim().toUpperCase();
        const r = await axios.get(`${API}/w-verify/${cleanHash}`);
        setResult(r.data);
      } catch (e) {
        setResult({ verified: false, message: 'Erro ao verificar' });
      } finally {
        setLoading(false);
      }
    };
    verify();
  }, [hash]);

  const dateDisplay = result?.date ? new Date(result.date).toLocaleDateString('pt-BR') : '';
  const timeDisplay = result?.date ? new Date(result.date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className="min-h-screen bg-[#060810] flex items-center justify-center py-8 px-4" style={{ fontFamily: 'Jost, sans-serif' }}>
      <style>{`
        @keyframes wSealPulse {
          0%   { transform: scale(1);    filter: drop-shadow(0 0 0 rgba(96,165,250,0)); }
          25%  { transform: scale(1.12); filter: drop-shadow(0 0 8px rgba(96,165,250,0.6)); }
          50%  { transform: scale(0.98); filter: drop-shadow(0 0 4px rgba(96,165,250,0.3)); }
          75%  { transform: scale(1.04); filter: drop-shadow(0 0 6px rgba(96,165,250,0.4)); }
          100% { transform: scale(1);    filter: drop-shadow(0 0 0 rgba(96,165,250,0)); }
        }
      `}</style>      <div className="w-full max-w-md rounded-3xl overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, rgba(15,20,35,0.95), rgba(6,8,16,0.98))',
          border: '1px solid rgba(59,130,246,0.15)',
          boxShadow: '0 0 60px rgba(59,130,246,0.06), 0 20px 40px rgba(0,0,0,0.4)',
        }}
        data-testid="verify-card">
        {loading ? (
          <div className="py-16 text-center">
            <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto" />
            <p className="text-white/30 text-sm mt-4">Verificando...</p>
          </div>
        ) : result?.verified ? (
          <div className="p-6 sm:p-8 text-center">
            <CircularSeal
              percentage={result.percentage || 0}
              shortHash={(result.hash || '').toUpperCase()}
              date={dateDisplay}
            />

            <div className="flex items-center justify-center gap-2 mt-6 mb-3">
              <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-emerald-400 text-sm font-semibold">Selo Verificado</span>
            </div>
            <p className="text-white text-xl font-bold mb-1">{result.percentage}% Autenticidade</p>
            <p className="text-white/35 text-xs mb-4 font-mono">#{result.hash}</p>
            {result.member_name && <p className="text-white/60 text-sm">Verificado por {result.member_name.trim()}</p>}
            {result.date && <p className="text-white/30 text-xs mt-1">{dateDisplay} · {timeDisplay}</p>}
            {result.sequence && (
              <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full"
                style={{
                  background: 'linear-gradient(135deg, rgba(59,130,246,0.10), rgba(34,197,94,0.06))',
                  border: '1px solid rgba(59,130,246,0.18)',
                }}
                data-testid="verify-sequence-badge">
                <span className="text-white/40 text-[11px] uppercase tracking-widest">Produto autenticado</span>
                <span className="text-white text-sm font-bold">
                  #{(result.sequence || 0).toLocaleString('pt-BR')}
                </span>
                {result.total_verified ? (
                  <span className="text-white/35 text-[11px]">de {result.total_verified.toLocaleString('pt-BR')}</span>
                ) : null}
              </div>
            )}
            <div className="mt-6 pt-4 border-t border-white/[0.06]">
              <p className="text-white/25 text-[10px] uppercase tracking-widest">Verificado por W IA — WClub</p>
            </div>
          </div>
        ) : (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ background: 'rgba(239,68,68,0.12)', border: '1.5px solid rgba(239,68,68,0.3)' }}>
              <svg className="w-7 h-7 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-red-400 text-sm font-semibold mb-2">Não Verificado</p>
            <p className="text-white/40 text-xs">Hash #{hash} não encontrado no sistema WClub</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyPage;
